package de.xtl.retroguide.question

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.FilledTonalIconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.protocol.DotVote
import de.xtl.retroguide.protocol.VotingOption
import de.xtl.retroguide.protocol.toDotVote
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.Res
import retroguide.app.shared.generated.resources.dot_vote_add
import retroguide.app.shared.generated.resources.dot_vote_members
import retroguide.app.shared.generated.resources.dot_vote_no_options
import retroguide.app.shared.generated.resources.dot_vote_prompt
import retroguide.app.shared.generated.resources.dot_vote_remaining
import retroguide.app.shared.generated.resources.dot_vote_remove

@Composable
internal fun DotVotePicker(
    options: List<VotingOption>,
    budget: Int,
    value: String,
    enabled: Boolean,
    onValueChange: (String) -> Unit,
) {
    if (options.isEmpty()) {
        Text(
            stringResource(Res.string.dot_vote_no_options),
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        return
    }
    val draft = value.toDotVote() ?: DotVote(emptyMap())
    val remaining = (budget - draft.total).coerceAtLeast(0)
    Text(stringResource(Res.string.dot_vote_prompt), style = MaterialTheme.typography.titleMedium)
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        repeat(budget) { dot -> VoteDot(filled = dot >= remaining) }
        Text(
            stringResource(Res.string.dot_vote_remaining, remaining, budget),
            modifier = Modifier.padding(start = 6.dp),
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
    options.forEach { option ->
        val count = draft.votes[option.id] ?: 0
        Surface(
            shape = RoundedCornerShape(16.dp),
            color = if (count > 0) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Row(
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Text(option.label, style = MaterialTheme.typography.titleMedium)
                    option.category?.let { category ->
                        Text(
                            "${category.emoji} ${category.label()}",
                            style = MaterialTheme.typography.labelMedium,
                            color = category.accentColor,
                        )
                    } ?: option.memberCount.takeIf { it > 1 }?.let { members ->
                        Text(
                            stringResource(Res.string.dot_vote_members, members),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
                val removeLabel = stringResource(Res.string.dot_vote_remove, option.label)
                FilledTonalIconButton(
                    onClick = { onValueChange(draft.with(option.id, count - 1).encode()) },
                    enabled = enabled && count > 0,
                    modifier = Modifier.size(36.dp).semantics { contentDescription = removeLabel },
                ) { Text("−", style = MaterialTheme.typography.titleMedium) }
                Text(
                    count.toString(),
                    modifier = Modifier.padding(horizontal = 4.dp),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = if (count > 0) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                )
                val addLabel = stringResource(Res.string.dot_vote_add, option.label)
                FilledTonalIconButton(
                    onClick = { onValueChange(draft.with(option.id, count + 1).encode()) },
                    enabled = enabled && remaining > 0,
                    modifier = Modifier.size(36.dp).semantics { contentDescription = addLabel },
                ) { Text("+", style = MaterialTheme.typography.titleMedium) }
            }
        }
    }
}

@Composable
private fun VoteDot(filled: Boolean) {
    val color by animateColorAsState(
        if (filled) MaterialTheme.colorScheme.outlineVariant else MaterialTheme.colorScheme.primary,
    )
    Box(Modifier.size(14.dp).background(color, CircleShape))
}

/** Own vote: one filled dot per point in front of each option label. */
@Composable
internal fun DotVoteContent(vote: DotVote, options: List<VotingOption>) {
    val labels = options.associate { it.id to it.label }
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        vote.votes.forEach { (id, count) ->
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                repeat(count) { VoteDot(filled = false) }
                Text(
                    labels[id] ?: "?",
                    modifier = Modifier.padding(start = 6.dp),
                    style = MaterialTheme.typography.titleMedium,
                )
            }
        }
    }
}
